/**
 * Настройки `/settings` (§9.5): Telegram, общие, экспорт — одна страница,
 * три группы, у каждой своя кнопка «Сохранить».
 *
 * Токен бота — секрет: сервер отдаёт его маской и никогда целиком, поэтому
 * поле ввода всегда пустое, а подпись под ним говорит, что сейчас сохранено
 * и что старый токен будет заменён. Пустое поле = «оставить как было».
 * Проверка связи намеренно не смотрит на галочку «включено»: связь проверяют
 * до включения уведомлений.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ChangeSetSummary, ChannelCode } from '@shared/types.ts'
import {
  Badge, Button, Checkbox, ConsequenceLine, FieldRow, Input, SegmentedControl, Skeleton,
} from '../../components/index.js'
import { api } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { texts } from '../../texts.js'
import { AdminError, AdminGroup, AdminPage, AdminReason } from '../admin/AdminPage.js'
import { adminTexts } from '../admin/texts.admin.js'
import {
  CHANNEL_NAMES,
  errorText, formatDateTime, hasPermission, isRecord, loadSettings, loadTelegramStatus,
  readBool, readNumber, readString, saveSettings,
  type SettingsView, type TelegramStatusView,
} from '../admin/admin-api.js'
import './SettingsScreen.css'

const t = adminTexts.settings

/** Порядок галочек событий — как в §9.5. */
const EVENT_ORDER = [
  'brand_fields', 'brand_create', 'categories', 'comments', 'catalog', 'fields', 'users', 'revert',
] as const

type EventCode = typeof EVENT_ORDER[number]

type SortCode = 'alphabet' | 'tier'

interface TelegramForm {
  enabled: boolean
  /** Новый токен. Пусто — сохранённый не трогаем. */
  token: string
  chatId: string
  threadId: string
  quietEnabled: boolean
  quietFrom: string
  quietTo: string
  events: Record<EventCode, boolean>
}

interface CommonForm {
  publicUrl: string
  defaultChannel: ChannelCode
  defaultSort: SortCode
  pollIntervalSec: string
}

/** Сколько пакетов показываем в очереди уведомлений (§9.5). */
const QUEUE_LIMIT = 10

function readEvents(raw: unknown): Record<EventCode, boolean> {
  const source = isRecord(raw) ? raw : {}
  const out = {} as Record<EventCode, boolean>
  for (const code of EVENT_ORDER) out[code] = readBool(source[code], true)
  return out
}

function telegramFormOf(values: Record<string, unknown>): TelegramForm {
  const quiet = isRecord(values['telegram.quiet_hours']) ? values['telegram.quiet_hours'] : {}
  const threadId = readNumber(values['telegram.thread_id'])
  return {
    enabled: readBool(values['telegram.enabled'], false),
    token: '',
    chatId: readString(values['telegram.chat_id']) ?? '',
    threadId: threadId === null ? '' : String(threadId),
    quietEnabled: readBool(quiet.enabled, false),
    quietFrom: readString(quiet.from) ?? '22:00',
    quietTo: readString(quiet.to) ?? '09:00',
    events: readEvents(values['telegram.events']),
  }
}

function commonFormOf(values: Record<string, unknown>): CommonForm {
  const channel = readString(values['app.default_channel'])
  const sort = readString(values['app.default_sort'])
  const poll = readNumber(values['app.poll_interval_sec'])
  return {
    publicUrl: readString(values['app.public_url']) ?? '',
    defaultChannel: channel === 'b2b' ? 'b2b' : 'retail',
    defaultSort: sort === 'tier' ? 'tier' : 'alphabet',
    pollIntervalSec: poll === null ? '' : String(poll),
  }
}

function sameEvents(a: Record<EventCode, boolean>, b: Record<EventCode, boolean>): boolean {
  return EVENT_ORDER.every((code) => a[code] === b[code])
}

export function SettingsScreen() {
  const toast = useApp((s) => s.toast)
  const permissions = useApp((s) => s.bootstrap?.user.permissions)
  const canSeeHistory = hasPermission(permissions, 'history.view')

  const [view, setView] = useState<SettingsView | null>(null)
  const [status, setStatus] = useState<TelegramStatusView | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [telegram, setTelegram] = useState<TelegramForm | null>(null)
  const [common, setCommon] = useState<CommonForm | null>(null)

  const [savingTelegram, setSavingTelegram] = useState(false)
  const [savingCommon, setSavingCommon] = useState(false)
  const [testing, setTesting] = useState(false)
  const [packets, setPackets] = useState<ChangeSetSummary[] | null>(null)
  const [retrying, setRetrying] = useState<number | null>(null)

  const loadPackets = useCallback(async () => {
    if (!canSeeHistory) return
    try {
      const res = await api.changeSets({ page: 1 })
      setPackets(res.items.slice(0, QUEUE_LIMIT))
    } catch {
      // Очередь — вспомогательный блок: без него настройки работают.
      setPackets([])
    }
  }, [canSeeHistory])

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const [settingsView, statusView] = await Promise.all([loadSettings(), loadTelegramStatus()])
      setView(settingsView)
      setStatus(statusView)
      setTelegram(telegramFormOf(settingsView.values))
      setCommon(commonFormOf(settingsView.values))
      await loadPackets()
    } catch (err) {
      setLoadError(errorText(err))
    }
  }, [loadPackets])

  useEffect(() => { void load() }, [load])

  /** Маска сохранённого токена: «1234…abcd» или сообщение, что его ещё нет. */
  const tokenMask = useMemo(() => readString(view?.values['telegram.bot_token'] ?? null), [view])

  async function applyPatch(patch: Record<string, unknown>, done: (next: SettingsView) => void) {
    if (Object.keys(patch).length === 0) {
      toast('info', t.nothingToSave)
      return false
    }
    try {
      const next = await saveSettings(patch)
      setView(next)
      done(next)
      setStatus(await loadTelegramStatus())
      toast('success', t.savedToast)
      return true
    } catch (err) {
      toast('error', errorText(err))
      return false
    }
  }

  async function saveTelegram() {
    if (!telegram || !view) return
    const stored = telegramFormOf(view.values)
    const patch: Record<string, unknown> = {}

    if (telegram.enabled !== stored.enabled) patch['telegram.enabled'] = telegram.enabled
    if (telegram.token.trim().length > 0) patch['telegram.bot_token'] = telegram.token.trim()
    if (telegram.chatId.trim() !== stored.chatId) {
      patch['telegram.chat_id'] = telegram.chatId.trim() === '' ? null : telegram.chatId.trim()
    }
    if (telegram.threadId.trim() !== stored.threadId) {
      patch['telegram.thread_id'] = telegram.threadId.trim() === '' ? null : telegram.threadId.trim()
    }
    if (
      telegram.quietEnabled !== stored.quietEnabled ||
      telegram.quietFrom !== stored.quietFrom ||
      telegram.quietTo !== stored.quietTo
    ) {
      patch['telegram.quiet_hours'] = {
        enabled: telegram.quietEnabled,
        from: telegram.quietFrom.trim(),
        to: telegram.quietTo.trim(),
      }
    }
    if (!sameEvents(telegram.events, stored.events)) patch['telegram.events'] = telegram.events

    setSavingTelegram(true)
    await applyPatch(patch, (next) => setTelegram(telegramFormOf(next.values)))
    setSavingTelegram(false)
  }

  async function saveCommon() {
    if (!common || !view) return
    const stored = commonFormOf(view.values)
    const patch: Record<string, unknown> = {}

    if (common.publicUrl.trim() !== stored.publicUrl) patch['app.public_url'] = common.publicUrl.trim()
    if (common.defaultChannel !== stored.defaultChannel) patch['app.default_channel'] = common.defaultChannel
    if (common.defaultSort !== stored.defaultSort) patch['app.default_sort'] = common.defaultSort
    if (common.pollIntervalSec.trim() !== stored.pollIntervalSec) {
      const poll = Number(common.pollIntervalSec.trim())
      if (!Number.isInteger(poll)) {
        toast('error', t.pollIntervalHint)
        return
      }
      patch['app.poll_interval_sec'] = poll
    }

    setSavingCommon(true)
    await applyPatch(patch, (next) => setCommon(commonFormOf(next.values)))
    setSavingCommon(false)
  }

  async function sendTest() {
    setTesting(true)
    try {
      const res = await api.telegramTest()
      if (res.ok) toast('success', t.testOk)
      else toast('error', t.testFail(res.message))
      setStatus(await loadTelegramStatus())
    } catch (err) {
      toast('error', t.testFail(errorText(err)))
    } finally {
      setTesting(false)
    }
  }

  async function retry(changeSetId: number) {
    setRetrying(changeSetId)
    try {
      await api.notifyAgain(changeSetId)
      toast('success', t.queueRetryDone)
      setStatus(await loadTelegramStatus())
      await loadPackets()
    } catch (err) {
      toast('error', errorText(err))
    } finally {
      setRetrying(null)
    }
  }

  if (loadError) {
    return (
      <AdminPage title={texts.screen.settings.title}>
        <AdminError message={loadError} onRetry={() => { void load() }} />
      </AdminPage>
    )
  }

  if (!view || !telegram || !common) {
    return (
      <AdminPage title={texts.screen.settings.title}>
        <Skeleton rows={6} columns={[360, 240]} label={adminTexts.common.loading} />
      </AdminPage>
    )
  }

  const tokenChanged = telegram.token.trim().length > 0

  return (
    <AdminPage title={texts.screen.settings.title} subtitle={adminTexts.common.immediate}>
      {/* ─────────────────────────────── Telegram ─────────────────────────── */}
      <AdminGroup
        title={t.groupTelegram}
        lead={t.telegramLead}
        footer={
          <>
            <Button variant="primary" loading={savingTelegram} onClick={() => { void saveTelegram() }}>
              {t.saveGroup}
            </Button>
            <Button variant="secondary" loading={testing} onClick={() => { void sendTest() }}>
              {testing ? t.testing : t.test}
            </Button>
            {status && !status.configured ? (
              <span className="set-note t-meta">{t.testNeedsConfig}</span>
            ) : null}
          </>
        }
      >
        <Checkbox
          checked={telegram.enabled}
          label={t.enabled}
          hint={t.enabledHint}
          onChange={(checked) => setTelegram({ ...telegram, enabled: checked })}
        />

        <FieldRow
          label={t.token}
          htmlFor="tg-token"
          consequence={
            <ConsequenceLine tone={tokenChanged ? 'danger' : 'neutral'}>
              {tokenChanged ? t.tokenReplace : t.tokenKeep}
            </ConsequenceLine>
          }
        >
          <Input
            id="tg-token"
            type="password"
            autoComplete="off"
            value={telegram.token}
            placeholder={t.tokenPlaceholder}
            hint={tokenMask ? t.tokenCurrent(tokenMask) : t.tokenEmpty}
            onChange={(event) => setTelegram({ ...telegram, token: event.target.value })}
          />
        </FieldRow>

        <div className="adm-inline-fields">
          <FieldRow label={t.chatId} htmlFor="tg-chat">
            <Input
              id="tg-chat"
              inputMode="numeric"
              value={telegram.chatId}
              hint={t.chatIdHint}
              onChange={(event) => setTelegram({ ...telegram, chatId: event.target.value })}
            />
          </FieldRow>
          <FieldRow label={t.threadId} htmlFor="tg-thread">
            <Input
              id="tg-thread"
              inputMode="numeric"
              value={telegram.threadId}
              hint={t.threadIdHint}
              onChange={(event) => setTelegram({ ...telegram, threadId: event.target.value })}
            />
          </FieldRow>
        </div>

        <div className="adm-stack">
          <p className="adm-sub">{t.quietTitle}</p>
          <Checkbox
            checked={telegram.quietEnabled}
            label={t.quietEnabled}
            hint={telegram.quietEnabled ? undefined : t.quietOff}
            onChange={(checked) => setTelegram({ ...telegram, quietEnabled: checked })}
          />
          {telegram.quietEnabled ? (
            <div className="adm-inline-fields">
              <FieldRow label={t.quietFrom} htmlFor="tg-quiet-from">
                <Input
                  id="tg-quiet-from"
                  value={telegram.quietFrom}
                  hint={t.quietHint}
                  onChange={(event) => setTelegram({ ...telegram, quietFrom: event.target.value })}
                />
              </FieldRow>
              <FieldRow label={t.quietTo} htmlFor="tg-quiet-to">
                <Input
                  id="tg-quiet-to"
                  value={telegram.quietTo}
                  onChange={(event) => setTelegram({ ...telegram, quietTo: event.target.value })}
                />
              </FieldRow>
            </div>
          ) : null}
        </div>

        <div className="adm-stack">
          <p className="adm-sub" id="tg-events">{t.eventsTitle}</p>
          <div className="set-events" role="group" aria-labelledby="tg-events">
            {EVENT_ORDER.map((code) => (
              <Checkbox
                key={code}
                checked={telegram.events[code]}
                label={t.events[code]}
                onChange={(checked) => setTelegram({
                  ...telegram,
                  events: { ...telegram.events, [code]: checked },
                })}
              />
            ))}
          </div>
          {/* Это не настройка: напоминание работает всегда, выключателя на
              сервере нет. Выключенный чекбокс выглядел как сломанный контрол. */}
          <p className="set-note t-meta">{t.remindAlways}</p>
        </div>

        <div className="adm-stack adm-stack--tight">
          <p className="adm-sub">{t.queueTitle}</p>
          {status ? (
            <>
              <p className="t-meta set-note">
                {!status.configured
                  ? t.queueNotConfigured
                  : !status.enabled
                    ? t.queueOff
                    : status.pendingCount === 0 && status.failedCount === 0
                      ? t.queueOk
                      : `${t.queuePending(status.pendingCount)} · ${t.queueFailed(status.failedCount)}`}
              </p>
              {status.chatTitle ? (
                <p className="t-meta set-note">{t.queueChat(status.chatTitle)}</p>
              ) : null}
              {status.lastError ? (
                <ConsequenceLine tone="danger">{t.queueLastError(status.lastError)}</ConsequenceLine>
              ) : null}
            </>
          ) : null}

          {canSeeHistory && packets && packets.length > 0 ? (
            <ul className="set-queue">
              {packets.map((packet) => (
                <li key={packet.id} className="set-queue__row">
                  <span className="set-queue__id t-meta">{`№${packet.id}`}</span>
                  <span className="set-queue__when t-meta">
                    {formatDateTime(packet.committedAt) ?? ''}
                  </span>
                  <Badge variant={packet.telegramStatus === 'failed' ? 'danger' : 'neutral'}>
                    {t.tgStatus[packet.telegramStatus]}
                  </Badge>
                  <span className="set-queue__gap" />
                  <Button
                    size="s"
                    variant="ghost"
                    loading={retrying === packet.id}
                    /* Пока бот не настроен, отправлять некуда: кнопка не должна
                       выглядеть рабочей. Причина написана строкой выше. */
                    disabled={!status?.configured}
                    title={status?.configured ? undefined : t.queueNotConfigured}
                    onClick={() => { void retry(packet.id) }}
                  >
                    {t.queueRetry}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
          {!canSeeHistory ? <AdminReason>{t.queueNoHistory}</AdminReason> : null}
        </div>
      </AdminGroup>

      {/* ──────────────────────────────── общие ───────────────────────────── */}
      <AdminGroup
        title={t.groupCommon}
        footer={
          <Button variant="primary" loading={savingCommon} onClick={() => { void saveCommon() }}>
            {t.saveGroup}
          </Button>
        }
      >
        <FieldRow label={t.publicUrl} htmlFor="app-url" consequence={<ConsequenceLine>{t.publicUrlHint}</ConsequenceLine>}>
          <Input
            id="app-url"
            type="url"
            value={common.publicUrl}
            onChange={(event) => setCommon({ ...common, publicUrl: event.target.value })}
          />
        </FieldRow>

        <FieldRow label={t.defaultChannel} consequence={<ConsequenceLine>{t.defaultChannelHint}</ConsequenceLine>} group>
          <SegmentedControl<ChannelCode>
            label={t.defaultChannel}
            value={common.defaultChannel}
            onChange={(value) => setCommon({ ...common, defaultChannel: value })}
            options={[
              { value: 'retail', label: CHANNEL_NAMES.retail },
              { value: 'b2b', label: CHANNEL_NAMES.b2b },
            ]}
          />
        </FieldRow>

        <FieldRow label={t.defaultSort} group>
          <SegmentedControl<SortCode>
            label={t.defaultSort}
            value={common.defaultSort}
            onChange={(value) => setCommon({ ...common, defaultSort: value })}
            options={[
              { value: 'alphabet', label: t.sortAlphabet },
              { value: 'tier', label: t.sortTier },
            ]}
          />
        </FieldRow>

        <FieldRow label={t.pollInterval} htmlFor="app-poll" consequence={<ConsequenceLine>{t.pollIntervalHint}</ConsequenceLine>}>
          <Input
            id="app-poll"
            numeric
            inputMode="numeric"
            className="set-narrow"
            value={common.pollIntervalSec}
            onChange={(event) => setCommon({ ...common, pollIntervalSec: event.target.value })}
          />
        </FieldRow>
      </AdminGroup>

      {/* ─────────────────────────────── экспорт ──────────────────────────── */}
      <AdminGroup title={t.groupExport}>
        <div className="set-export">
          <a className="set-export__link t-btn" href={api.exportXlsxUrl()} download>
            {t.exportSliceXlsx}
          </a>
          <a className="set-export__link t-btn" href={api.exportCsvUrl()} download>
            {t.exportSlice}
          </a>
          <div className="set-export__off">
            <Button variant="secondary" disabled>{t.exportLog}</Button>
            <AdminReason>{t.exportLogReason}</AdminReason>
          </div>
          <div className="set-export__off">
            <Button variant="secondary" disabled>{t.exportXlsx}</Button>
            <AdminReason>{t.stage2}</AdminReason>
          </div>
          <div className="set-export__off">
            <Button variant="secondary" disabled>{t.exportSheets}</Button>
            <AdminReason>{t.stage2}</AdminReason>
          </div>
        </div>
      </AdminGroup>
    </AdminPage>
  )
}
